import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { FormAsyncValidateOrFn, FormValidateOrFn } from "@tanstack/react-form";
import { BoomerFormContext } from "@/form/boomer/context";
import type { AnyBoomerFormApi } from "@/form/boomer/types";
import { useAppForm } from "@/form/common/hook";
import { isFormValuesDirty } from "@/form/common/utils";
import { cn } from "@/lib/style/cva.config";

export function useBoomerForm<
  TFormData,
  TOnMount extends undefined | FormValidateOrFn<TFormData>,
  TOnChange extends undefined | FormValidateOrFn<TFormData>,
  TOnChangeAsync extends undefined | FormAsyncValidateOrFn<TFormData>,
  TOnBlur extends undefined | FormValidateOrFn<TFormData>,
  TOnBlurAsync extends undefined | FormAsyncValidateOrFn<TFormData>,
  TOnSubmit extends undefined | FormValidateOrFn<TFormData>,
  TOnSubmitAsync extends undefined | FormAsyncValidateOrFn<TFormData>,
  TOnServer extends undefined | FormAsyncValidateOrFn<TFormData>,
  TSubmitMeta,
>(
  params: Parameters<
    typeof useAppForm<
      TFormData,
      TOnMount,
      TOnChange,
      TOnChangeAsync,
      TOnBlur,
      TOnBlurAsync,
      TOnSubmit,
      TOnSubmitAsync,
      TOnServer,
      TSubmitMeta
    >
  >[0],
  options?: { ref?: HTMLFormElement; initialIsValuesDirty?: boolean },
) {
  const [state, setState] = useContext(BoomerFormContext);
  const ref = useRef<HTMLFormElement>(options?.ref ?? null);
  const [isValuesDirty, setIsValuesDirty] = useState(options?.initialIsValuesDirty ?? false);

  const form = useAppForm<
    TFormData,
    TOnMount,
    TOnChange,
    TOnChangeAsync,
    TOnBlur,
    TOnBlurAsync,
    TOnSubmit,
    TOnSubmitAsync,
    TOnServer,
    TSubmitMeta
  >(params);

  useEffect(
    () => form.store.subscribe(() => setIsValuesDirty(isFormValuesDirty(form))),
    [form, setIsValuesDirty],
  );

  const submit = useCallback(() => {
    if (ref.current) {
      ref.current.requestSubmit();
    }
  }, [ref.current]);

  const Element = useCallback(
    (props: React.ComponentProps<"form">) => (
      <form.AppForm>
        <form
          ref={ref}
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            form.handleSubmit();
          }}
          {...props}
          className={cn("contents", props.className)}
        />
      </form.AppForm>
    ),
    [form, ref],
  );

  const result = useMemo(
    () => [form, { ref, isValuesDirty, submit, Element }] as const,
    [form, ref, isValuesDirty, submit, Element],
  );

  useEffect(() => {
    if (!state) {
      // result is too-specific; cast to UseBoomerFormResult
      setState(result as AnyBoomerFormApi);
    }
  }, [result, state, setState]);

  return result;
}
